import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, ChevronDown, Check, X, Search } from 'lucide-react';
import { useProperties } from '../Widgets/filters/useProperties';
import './property-filter.css';

/**
 * Multi-select property dropdown. Emits selected IDs array.
 * When nothing selected → treated as "All Portfolio".
 */
export default function PropertyFilter({ value = [], onChange, className = '' }) {
    const { t } = useTranslation();
    const { properties, loading } = useProperties();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const ref = useRef(null);
    const searchRef = useRef(null);

    const selected = useMemo(() => new Set(value), [value]);
    const allSelected = selected.size === 0;

    const filteredProperties = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return properties;
        return properties.filter(p =>
            (p.name || '').toLowerCase().includes(q) ||
            (p.code || '').toLowerCase().includes(q)
        );
    }, [properties, query]);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
                setQuery('');
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    useEffect(() => {
        if (open && searchRef.current) {
            const t = setTimeout(() => searchRef.current?.focus(), 0);
            return () => clearTimeout(t);
        }
    }, [open]);

    const toggle = useCallback((id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id); else next.add(id);
        onChange([...next]);
    }, [selected, onChange]);

    const selectAll = useCallback(() => onChange([]), [onChange]);

    const label = allSelected
        ? t('All Portfolio')
        : selected.size === 1
            ? (properties.find(p => p.id === [...selected][0])?.name || t('1 property'))
            : t('{{count}} properties', { count: selected.size });

    return (
        <div className={`v3-pf ${className}`} ref={ref}>
            <button
                type="button"
                className={`v3-pf-trigger ${open ? 'open' : ''}`}
                onClick={() => setOpen(o => !o)}
            >
                <Building2 size={14} className="v3-pf-icon" />
                <span className="v3-pf-label">{label}</span>
                {!allSelected && (
                    <span className="v3-pf-count">{selected.size}</span>
                )}
                <ChevronDown size={13} className={`v3-pf-chevron ${open ? 'flipped' : ''}`} />
            </button>

            {open && (
                <div className="v3-pf-dropdown">
                    <div className="v3-pf-search">
                        <Search size={13} className="v3-pf-search-icon" />
                        <input
                            ref={searchRef}
                            type="text"
                            className="v3-pf-search-input"
                            placeholder={t('Search properties…')}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                        {query && (
                            <button
                                type="button"
                                className="v3-pf-search-clear"
                                onClick={() => setQuery('')}
                                aria-label={t('Clear search')}
                            >
                                <X size={12} />
                            </button>
                        )}
                    </div>

                    {!query && (
                        <button
                            type="button"
                            className={`v3-pf-option ${allSelected ? 'selected' : ''}`}
                            onClick={selectAll}
                        >
                            <span className={`v3-pf-check ${allSelected ? 'on' : ''}`}>
                                {allSelected && <Check size={11} />}
                            </span>
                            <span>{t('All Portfolio')}</span>
                        </button>
                    )}

                    {loading && <div className="v3-pf-loading">{t('Loading…')}</div>}

                    {!loading && filteredProperties.length === 0 && (
                        <div className="v3-pf-loading">{t('No matches')}</div>
                    )}

                    {filteredProperties.map(p => {
                        const isOn = selected.has(p.id);
                        return (
                            <button
                                key={p.id}
                                type="button"
                                className={`v3-pf-option ${isOn ? 'selected' : ''}`}
                                onClick={() => toggle(p.id)}
                            >
                                <span className={`v3-pf-check ${isOn ? 'on' : ''}`}>
                                    {isOn && <Check size={11} />}
                                </span>
                                <span className="v3-pf-opt-name">{p.name}</span>
                                {p.code && <span className="v3-pf-opt-code">{p.code}</span>}
                            </button>
                        );
                    })}

                    {!allSelected && (
                        <div className="v3-pf-footer">
                            <button type="button" className="v3-pf-clear" onClick={selectAll}>
                                <X size={12} /> {t('Clear')}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
